import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AgeGroup, Child, TierProgressionMode } from '../types/child';
import type { CompletionRecord } from '../types/progress';
import { ageGroupFromBirthDate } from '../utils/date';
import { buildAdvancementEntry, catchUpToAge } from '../lib/tier-progression';
import { useProgressStore } from './progressStore';
import { useProjectStore, type ProjectProgress } from './projectStore';
import { useParkPassportStore, type ParkVisit } from './parkPassportStore';
import { deletePhotos, isIdbPhoto, photoKey } from '../utils/photo-db';
import { useCompetitionStore } from './competitionStore';
import { useArcadeStore } from './arcadeStore';
import { useMilestoneToastStore } from './milestoneToastStore';
import { logBetaEvent } from '../lib/beta-ops';

/**
 * Fire-and-forget push to Supabase so child profile edits propagate across
 * devices. Lazy-imported to keep the sync bundle out of the cold-start path
 * for signed-out users; no-ops on the sync side when there's no session.
 *
 * Debounced because `updateChild` is called on every xp/streak/totalMinutes
 * tick — bursting one request per increment would saturate the network and
 * the Supabase rate limit. We coalesce to one push per quiet window.
 */
const PUSH_DEBOUNCE_MS = 1500;
let pushTimer: ReturnType<typeof setTimeout> | null = null;
function pushChildrenAsync(): void {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void import('../engine/sync-adapter')
      .then(m => m.triggerChildSync())
      .catch(() => { /* fire-and-forget; sync-adapter handles its own logging */ });
  }, PUSH_DEBOUNCE_MS);
}

const STREAK_MILESTONES: { days: number; minutes: number; label: string }[] = [
  { days: 7,   minutes: 10, label: '7-Day Streak! 🔥' },
  { days: 30,  minutes: 30, label: '30-Day Streak! 🔥🔥' },
  { days: 100, minutes: 60, label: '100-Day Streak! 🔥🔥🔥' },
  { days: 365, minutes: 120, label: '365-Day Streak! 🏆' },
];

// Length caps — applied at the store boundary so any UI bypass (or a
// programmatic call from a tampered store import) is still constrained.
const MAX_NAME = 60;
const MAX_INTEREST = 40;
const MAX_INTERESTS = 20;
const cap = (s: string, n: number) => (typeof s === 'string' ? s.slice(0, n) : '');

/**
 * Soft-delete TTL. After this many days in the trash, the child and all
 * their data are permanently purged on next app boot.
 *
 * MUST stay aligned with the public-facing retention promise in
 * `src/pages/legal/PrivacyPolicyPage.tsx` and
 * `src/pages/legal/CoppaNoticePage.tsx` — both currently tell parents
 * "30 days, then permanent deletion." If you change this constant, update
 * the legal pages and `DataRightsPanel.tsx` copy in the same PR.
 */
export const TRASH_TTL_DAYS = 30;
const TRASH_TTL_MS = TRASH_TTL_DAYS * 24 * 60 * 60 * 1000;
const AGE_GROUPS = new Set<AgeGroup>(['pre-k', 'kinder', 'early-elementary', 'upper-elementary']);

/**
 * A trashed child carries snapshots of every collection that referenced
 * them, so a `restoreChild` call is fully reversible. Photos in IndexedDB
 * are NOT touched until permanent purge — we keep the original photoIds so
 * the cleanup pass knows what to delete.
 */
export interface DeletedChildEntry {
  child: Child;
  deletedAt: string; // ISO timestamp
  completions: CompletionRecord[];
  projects: ProjectProgress[];
  parkVisits: ParkVisit[];
  /** IDB photo keys that should be deleted on permanent purge. */
  photoIds: string[];
}

interface ChildState {
  children: Child[];
  activeChildId: string | null;
  deletedChildren: DeletedChildEntry[];

  addChild: (name: string, birthDate: string, avatarEmoji: string, interests?: string[], state?: string) => Child;
  /** @deprecated Use softDeleteChild. Kept for backwards compatibility. */
  removeChild: (id: string) => void;
  softDeleteChild: (id: string) => void;
  restoreChild: (id: string) => void;
  permanentlyDeleteChild: (id: string) => void;
  /** Run on app boot. Purges trash entries past TRASH_TTL_DAYS. */
  purgeExpiredTrash: () => void;
  setActiveChild: (id: string) => void;
  getActiveChild: () => Child | null;
  updateChild: (id: string, updates: Partial<Child>) => void;

  /** Parent dash setter for the tier progression mode. */
  setTierProgressionMode: (childId: string, mode: TierProgressionMode) => void;
  /**
   * Bump the child's effective tier. Caller is responsible for deciding
   * eligibility (see `lib/tier-progression.ts`). Records the advancement
   * in `tierAdvancementHistory`. No-op if the child doesn't exist or the
   * target tier isn't strictly higher than current.
   */
  advanceTier: (childId: string, toTier: AgeGroup, trigger: TierProgressionMode) => void;
  /**
   * Bring every child up to their birthdate-derived floor. Run on app boot
   * so a child who aged up while the app was closed sees the new content
   * tier without waiting for a manual edit.
   */
  catchUpAllToAge: () => void;
}

function collectChildPhotoIds(completions: CompletionRecord[]): string[] {
  return completions
    .filter(c => c.photoUrl && isIdbPhoto(c.photoUrl))
    .map(c => photoKey(c.photoUrl!));
}

function normalizeAgeGroup(child: Partial<Child>): AgeGroup {
  return typeof child.ageGroup === 'string' && AGE_GROUPS.has(child.ageGroup as AgeGroup)
    ? child.ageGroup as AgeGroup
    : ageGroupFromBirthDate(child.birthDate ?? new Date().toISOString().slice(0, 10));
}

export const useChildStore = create<ChildState>()(
  persist(
    (set, get) => ({
      children: [],
      activeChildId: null,
      deletedChildren: [],

      addChild: (name, birthDate, avatarEmoji, interests = [], state) => {
        const ageGroup = ageGroupFromBirthDate(birthDate);
        const safeName = cap(name, MAX_NAME).trim() || 'Friend';
        const safeInterests = (interests || [])
          .slice(0, MAX_INTERESTS)
          .map(i => cap(i, MAX_INTEREST))
          .filter(Boolean);
        const child: Child = {
          id: crypto.randomUUID(),
          name: safeName,
          birthDate,
          ageGroup,
          avatarEmoji: cap(avatarEmoji, 8),
          createdAt: new Date().toISOString(),
          interests: safeInterests,
          ...(state ? { state } : {}),
          xp: 0,
          level: 1,
          currentStreak: 0,
          longestStreak: 0,
          lastActiveDate: null,
          totalActivitiesCompleted: 0,
          totalMinutesLearned: 0,
          badgeIds: [],
        };
        set(state => ({
          children: [...state.children, child],
          activeChildId: state.activeChildId || child.id,
        }));
        logBetaEvent('child.created', {
          childId: child.id,
          ageGroup: child.ageGroup,
          interestCount: child.interests.length,
          hasState: !!child.state,
        });
        pushChildrenAsync();
        return child;
      },

      softDeleteChild: (id) => {
        const child = get().children.find(c => c.id === id);
        if (!child) return;

        // Snapshot every collection that referenced the child, then strip
        // those rows from the active stores. Photos in IDB are left alone
        // — they only get unlinked on permanent purge so a restore can
        // bring the portfolio back intact.
        const completions = useProgressStore.getState().completions.filter(c => c.childId === id);
        const projects = useProjectStore.getState().projects.filter(p => p.childId === id);
        const parkVisits = useParkPassportStore.getState().visits.filter(v => v.childId === id);
        const photoIds = collectChildPhotoIds(completions);

        useProgressStore.setState(s => ({
          completions: s.completions.filter(c => c.childId !== id),
        }));
        useProjectStore.setState(s => ({
          projects: s.projects.filter(p => p.childId !== id),
        }));
        useParkPassportStore.setState(s => ({
          visits: s.visits.filter(v => v.childId !== id),
        }));

        const entry: DeletedChildEntry = {
          child,
          deletedAt: new Date().toISOString(),
          completions,
          projects,
          parkVisits,
          photoIds,
        };

        set(state => {
          const remaining = state.children.filter(c => c.id !== id);
          return {
            children: remaining,
            deletedChildren: [...state.deletedChildren, entry],
            activeChildId: state.activeChildId === id
              ? (remaining[0]?.id || null)
              : state.activeChildId,
          };
        });
        pushChildrenAsync();
      },

      restoreChild: (id) => {
        const entry = get().deletedChildren.find(d => d.child.id === id);
        if (!entry) return;

        // Replay snapshots back into the active stores. Use functional
        // updates so concurrent edits during the trash window don't
        // clobber data added since the soft-delete.
        useProgressStore.setState(s => ({
          completions: [...s.completions, ...entry.completions],
        }));
        useProjectStore.setState(s => ({
          projects: [...s.projects, ...entry.projects],
        }));
        useParkPassportStore.setState(s => ({
          visits: [...s.visits, ...entry.parkVisits],
        }));

        set(state => ({
          children: [...state.children, entry.child],
          deletedChildren: state.deletedChildren.filter(d => d.child.id !== id),
          activeChildId: state.activeChildId || entry.child.id,
        }));
        pushChildrenAsync();
      },

      permanentlyDeleteChild: (id) => {
        const entry = get().deletedChildren.find(d => d.child.id === id);
        if (!entry) return;

        if (entry.photoIds.length > 0) {
          // Best-effort — IDB might be unavailable, but we still drop the
          // metadata so the trash entry doesn't stick around.
          deletePhotos(entry.photoIds).catch(() => {});
        }

        set(state => ({
          deletedChildren: state.deletedChildren.filter(d => d.child.id !== id),
        }));
      },

      purgeExpiredTrash: () => {
        const now = Date.now();
        const expired: DeletedChildEntry[] = [];
        const kept: DeletedChildEntry[] = [];
        for (const entry of get().deletedChildren) {
          if (now - new Date(entry.deletedAt).getTime() > TRASH_TTL_MS) {
            expired.push(entry);
          } else {
            kept.push(entry);
          }
        }
        if (expired.length === 0) return;

        const allPhotoIds = expired.flatMap(e => e.photoIds);
        if (allPhotoIds.length > 0) {
          deletePhotos(allPhotoIds).catch(() => {});
        }
        set({ deletedChildren: kept });
      },

      // Backwards-compat shim. Old code that called removeChild now goes
      // through soft delete instead of an irreversible purge.
      removeChild: (id) => {
        get().softDeleteChild(id);
      },

      setActiveChild: (id) => set({ activeChildId: id }),

      getActiveChild: () => {
        const { children, activeChildId } = get();
        return children.find(c => c.id === activeChildId) || null;
      },

      updateChild: (id, updates) => {
        const safe: Partial<Child> = { ...updates };
        if (typeof safe.name === 'string') safe.name = cap(safe.name, MAX_NAME);
        if (Array.isArray(safe.interests)) {
          safe.interests = safe.interests.slice(0, MAX_INTERESTS).map(i => cap(i, MAX_INTEREST));
        }
        if (typeof safe.avatarEmoji === 'string') safe.avatarEmoji = cap(safe.avatarEmoji, 8);
        set(state => ({
          children: state.children.map(c =>
            c.id === id ? { ...c, ...safe } : c
          ),
        }));
        pushChildrenAsync();
        // Bridge streak changes to competition store + milestone rewards
        if (typeof safe.currentStreak === 'number') {
          const prev = get().children.find(c => c.id === id)?.currentStreak ?? 0;
          if (safe.currentStreak > prev) {
            // Competition event submission
            const comp = useCompetitionStore.getState();
            for (const evt of comp.getActiveEventsForQuiz('daily-streak')) {
              const best = comp.results
                .filter(r => r.eventId === evt.id && r.childId === id)
                .reduce((m, r) => Math.max(m, r.score), -1);
              if (safe.currentStreak > best) {
                comp.submitResult(evt.id, id, safe.currentStreak, []);
              }
            }
            // Milestone game-time rewards
            for (const ms of STREAK_MILESTONES) {
              if (prev < ms.days && safe.currentStreak >= ms.days) {
                useArcadeStore.getState().addParentBonus(id, ms.minutes);
                useMilestoneToastStore.getState().show(ms.label, ms.minutes);
              }
            }
          }
        }
      },

      setTierProgressionMode: (childId, mode) => {
        set(state => ({
          children: state.children.map(c =>
            c.id === childId ? { ...c, tierProgressionMode: mode } : c,
          ),
        }));
      },

      advanceTier: (childId, toTier, trigger) => {
        set(state => ({
          children: state.children.map(c => {
            if (c.id !== childId) return c;
            // Only advance UP — never demote.
            if (AGE_GROUPS.has(c.ageGroup) === false) return c;
            const history = [...(c.tierAdvancementHistory ?? []), buildAdvancementEntry(toTier, trigger)];
            return { ...c, ageGroup: toTier, tierAdvancementHistory: history };
          }),
        }));
        logBetaEvent('child.tier-advanced', { childId, toTier, trigger });
      },

      catchUpAllToAge: () => {
        set(state => ({
          children: state.children.map(c => {
            const target = catchUpToAge(c);
            if (target === c.ageGroup) return c;
            const history = [...(c.tierAdvancementHistory ?? []), buildAdvancementEntry(target, 'age')];
            return { ...c, ageGroup: target, tierAdvancementHistory: history };
          }),
        }));
      },
    }),
    {
      name: 'homeschool-children',
      version: 5,
      // Run the trash purge once the persisted state has actually been
      // rehydrated. This is robust against any future async-storage swap
      // (e.g. localforage) where the boot-time call in main.tsx would race
      // the rehydrate and silently no-op against empty in-memory state.
      onRehydrateStorage: () => (state) => {
        if (state) state.purgeExpiredTrash();
      },
      migrate: (persisted: unknown, version: number) => {
        const state = persisted as {
          children?: Child[];
          activeChildId?: string | null;
          deletedChildren?: DeletedChildEntry[];
        };
        if (version < 2 && state?.children) {
          state.children = state.children.map(c => ({
            ...c,
            interests: (c as Child).interests || [],
            ageGroup: normalizeAgeGroup(c as Partial<Child>),
          }));
        }
        if (version < 3) {
          // v2 → v3: introduce trash slice. Existing data is unchanged;
          // we just initialise the new array.
          state.deletedChildren = state.deletedChildren || [];
        }
        if (version < 4 && state?.children) {
          // v3 → v4: backfill gamification/progress defaults on any legacy
          // child record that pre-dates these fields. Prevents NaN/100 in
          // the XP bar and similar undefined-arithmetic bugs.
          state.children = state.children.map(c => {
            const anyC = c as Partial<Child>;
            return {
              ...c,
              xp: Number.isFinite(anyC.xp) ? (anyC.xp as number) : 0,
              level: Number.isFinite(anyC.level) ? (anyC.level as number) : 1,
              currentStreak: Number.isFinite(anyC.currentStreak) ? (anyC.currentStreak as number) : 0,
              longestStreak: Number.isFinite(anyC.longestStreak) ? (anyC.longestStreak as number) : 0,
              lastActiveDate: anyC.lastActiveDate ?? null,
              totalActivitiesCompleted: Number.isFinite(anyC.totalActivitiesCompleted) ? (anyC.totalActivitiesCompleted as number) : 0,
              totalMinutesLearned: Number.isFinite(anyC.totalMinutesLearned) ? (anyC.totalMinutesLearned as number) : 0,
              badgeIds: Array.isArray(anyC.badgeIds) ? anyC.badgeIds as string[] : [],
              // birthDate wasn't always present — if missing, derive from the
              // legacy `dateOfBirth` alias some early records used.
              birthDate: c.birthDate ?? ((c as unknown as { dateOfBirth?: string }).dateOfBirth ?? new Date().toISOString().slice(0, 10)),
            } as Child;
          });
        }
        if (version < 5 && state?.children) {
          state.children = state.children.map(c => ({
            ...c,
            ageGroup: normalizeAgeGroup(c as Partial<Child>),
          }));
        }
        return state as unknown as ChildState;
      },
    }
  )
);
